import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon, CircleHelpIcon, PlayIcon } from "lucide-react";

import { Button } from "@components/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@components/Collapsible";
import { Image } from "@components/Image";
import { Typography } from "@components/Typography";

import { EToySessionState, type TLastStop } from "@api/toys";

import { cn } from "@lib/utils";

import { SKELETON_BLOCK_CLASS } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";
import {
	EProviderName,
	deviceStatusTextDescriptionMap,
	deviceTextStatusColorMap,
	deviceTextStatusMap,
} from "@configs/toys";

import { ToyActuatorControls } from "./ToyActuatorControls";
import { ToyDescription } from "./ToyDescription";
import { useToysControls } from "./useToysControls";

const deviceStatusIconActionMap = (
	providerName: string,
	handleOpenPlay: () => void,
	handleStop: () => void,
	sessionState: EToySessionState,
	isActionPending: boolean,
	lastStop?: TLastStop | null,
) => {
	switch (sessionState) {
		case EToySessionState.GRANTED_STOPPED:
			return lastStop?.by ? (
				<Button
					aria-label={`Open ${providerName} controls`}
					type="primary"
					shape="circle"
					size="auto"
					disable={isActionPending}
					onClick={handleOpenPlay}
					className="relative size-8! shrink-0 p-0! focus-visible:ring-2 focus-visible:ring-white"
				>
					<PlayIcon aria-hidden="true" className="size-5 translate-x-px" fill="currentColor" strokeWidth={0} />
				</Button>
			) : (
				<Button
					aria-label={`Open ${providerName} controls`}
					type="primary"
					shape="circle"
					size="auto"
					disable={isActionPending}
					onClick={handleOpenPlay}
					className="relative size-8! shrink-0 p-0! focus-visible:ring-2 focus-visible:ring-white"
				>
					<PlayIcon aria-hidden="true" className="size-5 translate-x-px" fill="currentColor" strokeWidth={0} />
				</Button>
			);
		case EToySessionState.GRANTED_RUNNING:
			return (
				<Button
					aria-label={`Stop ${providerName}`}
					type="red100"
					shape="circle"
					size="auto"
					disable={isActionPending}
					className="size-8! p-0!"
					onClick={handleStop}
				>
					<span aria-hidden="true" className="size-3.5 rounded-xs bg-white" />
				</Button>
			);
		case EToySessionState.IDLE:
		case EToySessionState.PENDING:
			return (
				<Image
					width={32}
					height={32}
					src={ICON_PATHS.PROVIDER_IS_DISABLED}
					alt={`${providerName} control disabled`}
					className="size-8 -scale-y-100 rotate-180"
				/>
			);
		case EToySessionState.SUSPENDED_OFFLINE:
			return (
				<Image
					width={40}
					height={40}
					src={ICON_PATHS.PROVIDER_IS_OFFLINE}
					alt={`${providerName} control disabled`}
					className="size-10 -scale-y-100 rotate-180"
				/>
			);
		case EToySessionState.CLOSED:
		default:
			return (
				<Image
					width={32}
					height={32}
					src={ICON_PATHS.PROVIDER_IS_DISABLED}
					alt={`${providerName} control disabled`}
					className="size-8 -scale-y-100 rotate-180"
				/>
			);
	}
};

const providerLogoMap = {
	[EProviderName.HANDY]: ICON_PATHS.HANDY_LOGO_DESKTOP,
	[EProviderName.AUTOBLOW]: ICON_PATHS.AUTOBLOW_LOGO_DESKTOP,
};

export const ToysContainer = () => {
	const {
		isGuideOpen,
		guideId,
		contentRef,
		isOpen,
		isOpenToyContainer,
		providerName,
		session,
		capabilities,
		sessionToken,
		isLoading,
		isError,
		sessionState,
		isToyContainerDisabled,
		isActionPending,
		handleToggleGuide,
		handleOpenPlay,
		handleStop,
		handleOpenChange,
	} = useToysControls();

	if (!isOpenToyContainer) {
		return null;
	}

	return (
		<Collapsible
			open={isOpen}
			onOpenChange={handleOpenChange}
			aria-disabled={isToyContainerDisabled}
			aria-busy={isLoading}
			className="flex w-77.5 max-w-full flex-col rounded-2xl bg-block p-5 text-white"
		>
			<header className="flex w-full shrink-0 flex-col items-center gap-2">
				<div className="flex h-8 w-full items-center justify-between">
					<CollapsibleTrigger
						aria-label={isOpen ? `Collapse ${providerName} controls` : `Expand ${providerName} controls`}
						className={`flex size-6 cursor-pointer items-center justify-start rounded-sm outline-none
							focus-visible:ring-2 focus-visible:ring-primary`}
					>
						{isOpen ? (
							<ChevronDownIcon aria-hidden="true" className="size-6" strokeWidth={1.5} />
						) : (
							<ChevronUpIcon aria-hidden="true" className="size-6" strokeWidth={1.5} />
						)}
					</CollapsibleTrigger>
					<Image
						width={129}
						height={32}
						src={providerLogoMap[providerName]}
						alt={`The ${providerName}`}
						className="h-8 w-32.25"
					/>
					{isLoading ? (
						<div className={cn(SKELETON_BLOCK_CLASS, "size-8 rounded-full")} aria-label="Loading toy status" />
					) : (
						deviceStatusIconActionMap(
							providerName,
							handleOpenPlay,
							handleStop,
							sessionState,
							isActionPending,
							session?.last_stop,
						)
					)}
				</div>
			</header>

			<div
				inert={isOpen}
				aria-hidden={isOpen}
				className={cn(
					"shrink-0 overflow-hidden transition-[height,opacity] duration-300 ease-in-out motion-reduce:transition-none",
					isOpen ? "h-0 opacity-0" : "h-13.5 opacity-100",
				)}
			>
				<CollapsibleTrigger
					aria-label={`Expand ${providerName} control panel`}
					className={`mt-5 w-full cursor-pointer rounded-sm text-left outline-none focus-visible:ring-2
						focus-visible:ring-primary`}
				>
					<Typography type="span" size="sm" color="grey-tertiary" className="block leading-4.25">
						Interactive toy control panel.
						<br />
						Tap to open the device controls
					</Typography>
				</CollapsibleTrigger>
			</div>

			<CollapsibleContent
				className={`min-h-0 shrink-0 duration-300 ease-in-out data-ending-style:duration-300
					data-ending-style:ease-in-out motion-reduce:transition-none`}
			>
				{/* The total height: 638px + 32px header + 40px padding = 710px. */}
				<div className="flex h-159.5 flex-col pt-2">
					{isLoading ? (
						<>
							<Typography type="span" role="status" className="sr-only">
								Loading toy controls
							</Typography>
							<div className="flex h-full flex-col items-center gap-5" aria-hidden="true">
								<div className={cn(SKELETON_BLOCK_CLASS, "h-6 w-28 shrink-0")} />
								<div className="flex h-12.75 w-full shrink-0 flex-col gap-2">
									<div className={cn(SKELETON_BLOCK_CLASS, "h-3 w-full")} />
									<div className={cn(SKELETON_BLOCK_CLASS, "h-3 w-4/5")} />
								</div>
								<div className={cn(SKELETON_BLOCK_CLASS, "h-8 w-32 shrink-0")} />
								<div className="flex min-h-0 w-full flex-1 items-center justify-center">
									<div className={cn(SKELETON_BLOCK_CLASS, "h-full max-h-50 w-32 rounded-2xl")} />
								</div>
								<div className="flex w-full shrink-0 flex-col gap-5 pb-2">
									<div className={cn(SKELETON_BLOCK_CLASS, "h-4 w-24")} />
									<div className={cn(SKELETON_BLOCK_CLASS, "h-6 w-full rounded-full")} />
									<div className={cn(SKELETON_BLOCK_CLASS, "h-4 w-28")} />
									<div className={cn(SKELETON_BLOCK_CLASS, "h-6 w-full rounded-full")} />
								</div>
							</div>
						</>
					) : isError || !session || !capabilities || !sessionToken ? (
						<Typography role="status" size="sm" color="grey-tertiary" className="py-5">
							Unable to load toy controls. Please try again later.
						</Typography>
					) : (
						<>
							<button
								type="button"
								aria-label={isGuideOpen ? `Back to ${providerName} controls` : `Open ${providerName} how-to guides`}
								aria-expanded={isGuideOpen}
								aria-controls={guideId}
								onClick={handleToggleGuide}
								className={cn(
									`relative flex h-6 shrink-0 cursor-pointer items-center justify-center gap-1 self-center rounded-sm
										outline-none focus-visible:ring-2 focus-visible:ring-primary`,
									isGuideOpen && "w-full",
								)}
							>
								{isGuideOpen ? (
									<ArrowLeftIcon aria-hidden="true" className="absolute left-0 size-6" strokeWidth={1.5} />
								) : null}
								<Typography type="span" size="sm" color="msg_time" weight="bold">
									How-to guides
								</Typography>
								{!isGuideOpen ? (
									<span className="flex size-6 items-center justify-center">
										<CircleHelpIcon aria-hidden="true" className="size-4" />
									</span>
								) : null}
							</button>
							<div className="mt-5 flex min-h-0 flex-1 flex-col items-center gap-5">
								{!isGuideOpen ? (
									<Typography size="sm" color="grey-tertiary" className="h-12.75 w-full shrink-0 leading-4.25">
										{deviceStatusTextDescriptionMap(sessionState, session.last_stop)}
									</Typography>
								) : null}

								<Typography
									type="p"
									size="sm"
									weight="bold"
									align="center"
									className={cn(
										"flex h-8 w-full shrink-0 items-center justify-center leading-4 tracking-[1.26px] uppercase",
										deviceTextStatusColorMap(sessionState, session.last_stop),
									)}
								>
									{deviceTextStatusMap(sessionState, session.last_stop)}
								</Typography>

								<div
									className={cn(
										"relative min-h-0 w-50 overflow-visible",
										isGuideOpen ? "h-50 shrink-0" : "flex-1",
										isToyContainerDisabled && !isGuideOpen && "opacity-30",
									)}
								>
									<Image
										width={isToyContainerDisabled ? 190 : 229}
										height={isToyContainerDisabled ? 190 : 229}
										src={ICON_PATHS.HANDY_DEVICE_IMAGE}
										alt={`The ${providerName} device`}
										className={cn(
											`absolute top-1/2 left-[calc(50%+0.5px)] max-h-full max-w-none -translate-x-1/2 -translate-y-1/2
												object-contain`,
											isToyContainerDisabled ? "size-47.5" : "size-57.25",
										)}
									/>
								</div>

								<div
									id={guideId}
									ref={contentRef}
									hidden={!isGuideOpen}
									role="region"
									aria-label={`${providerName} how-to guides`}
									tabIndex={isGuideOpen ? 0 : undefined}
									className="custom-scrollbar min-h-0 w-full flex-1 overflow-y-auto"
								>
									{isGuideOpen ? <ToyDescription /> : null}
								</div>

								<div
									className={cn(
										"flex w-full shrink-0 flex-col gap-5.25",
										isGuideOpen && "hidden",
										isToyContainerDisabled && "opacity-30",
									)}
								>
									{capabilities?.capabilities?.actuators.map((actuator) => (
										<ToyActuatorControls
											key={`${session.session_id}:${sessionToken}:${actuator.id}`}
											actuator={actuator}
											commands={session.motion?.actuators}
											disabled={isToyContainerDisabled}
											presets={capabilities.presets}
											sessionToken={sessionToken}
										/>
									))}
								</div>
							</div>
						</>
					)}
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
};
